<template lang="pug">
  .consultation-details
    .loading-overlay.fixed(v-if="showLoadingOverlay")
      v-progress-circular(:size="60" color="#eee" indeterminate)
      h3.mt-3 Bitte warten. Konzept für Beratung wird angelegt.

    v-layout.py-2.mb-6.consultation-header
      v-flex.sm6: h2.primary--text
        a.details-title(@dblclick="onClickTitle") {{ title }}

      v-flex.btn-actions.sm6(v-if="tabId > 0")
        a.btn-toggle.mr-3(
          v-if="isStatus"
          :class="showCustomerPref && 'selected'"
          @click="toggleShowCustomerPref"
        )
          svg-customer(:size="24")

        a.btn-toggle(
          v-if="isReloaded && !isConcept"
          :class="showEmptyNotice && 'selected'"
          @click="setShowEmptyNotice(!showEmptyNotice)"
        )
          svg-warning(:size="24")

        a(
          v-if="isConcept"
          @click="onRecreateConcept()"
        )
          i.fo.fo-reload

        a.btn-toggle.ml-3(
          v-if="isConcept"
          :class="isConceptEdit && 'selected'"
          @click="toggleConceptEdit(!isConceptEdit)"
        )
          i.fo.fo-calculator

        a.ml-3(v-if="tabId === 4 || tabId === 5" @click.prevent="showPrintModal = true"): i.fo.fo-print

    v-tabs.tab-header(v-model="tabId" @change="onChangeTab")
      template(v-for="(item, index) in tabItems")
        v-tab(:key="index" :disabled="index > 0 && (isNewStep || !inputDataLoaded)")
          span {{ item }}
          span(v-if="hasErrorFields(item)")
          n-error-tooltip.d-flex.ml-1(
            v-if="hasErrorFields(item)"
            :title="`Einige Feld-Eingaben fehlen oder sind fehlerhaft`"
          )

          svg-warning.svg-right-aligned.ml-2(v-if="tabId === index && hasEmptyField && showEmptyNotice" :size="20")

    company-details(
      v-show="tabId === 0"
      :id="id"
      @loaded="onDataLoaded"
      @next="onContinue"
    )

    template(v-for="(tab, tabIndex) in tabItems")
      input-section(
        v-if="tabId >= 1 && tabId <= 2 && tabId === tabIndex"
        :key="tab"
        :section-data="inputSection || {}"
        :title="tab"
        :prev-tab="tabIndex === 2 ? tabItems[tabIndex - 1] : ''"
        :next-tab="tabItems[tabIndex + 1]"
        @add-group="(id) => onAddGroup(id)"
        @del-group="(field_id, id) => onDeleteGroup(field_id, id)"
        @update="onSetField"
        @prev-tab="onChangeTab(tabId - 1)"
        @next-tab="onChangeTab(tabId + 1)"
      )

      output-section(
        v-else-if="tabId >= 3 && tabId <= 5 && tabId === tabIndex"
        :key="tab"
        :class="tabId === 3 && 'vertrag'"
        :categories="outputSection || []"
        :title="tab"
        :prev-tab="tabItems[tabIndex - 1]"
        :next-tab="tabId !== 5 ? tabItems[tabIndex + 1] : ''"
        @update="onSetField"
        @input-customer-pref="onInputCustomerPref"
        @prev-tab="onChangeTab(tabId - 1)"
        @next-tab="onChangeTab(tabId + 1)"
      )
        output-globals.mt-3(
          v-if="outputGlobals.length"
          :fields="outputGlobals"
          :koncept-fields="globalKonceptFields"
        )

    output-print(
      v-if="tabId === 4"
      v-model="showPrintModal"
      :id="id"
      :section-fields="outputSection || []"
      :global-fields="outputGlobals"
      :title="consultation.name"
      :client-name="clientName"
      :contact-data="consultation ? consultation.contact_data : ''"
    )

    konzept-print(
      v-if="tabId === 5"
      v-model="showPrintModal"
      :id="id"
      :section-fields="outputSection || []"
      :global-fields="outputGlobals"
      :title="consultation.name"
      :client-name="clientName"
      :contact-data="consultation ? consultation.contact_data : ''"
    )

</template>

<script>
import { Vue, Component, Prop } from 'vue-property-decorator'
import { mapActions } from 'vuex'

import AuthMixin from '@/mixins/auth'
import FormMixin from '@/mixins/form'
import Urls from '@/router/urls'
import consts from '@/store/common/consts'

@Component({
  mixins: [
    AuthMixin,
    FormMixin
  ],

  components: {
    CompanyDetails: () => import('./company-details'),
    InputSection: () => import('./components/section'),
    OutputSection: () => import('./components/output-section'),
    OutputGlobals: () => import('./components/output-globals'),
    OutputPrint: () => import('./components/output-print'),
    KonzeptPrint: () => import('./components/konzept-print'),
    SvgCustomer: () => import('@/components/svgs/svg-customer'),
    SvgWarning: () => import('@/components/svgs/svg-warning'),
  },

  methods: mapActions([
    'setDebugMode'
  ]),

  watch: {
    isDebugMode () {
      if (this.tabId <= consts.RISK_ANALYSIS) {
        this.inputSection = this.getInputSection(this.inputFields)
      } else {
        this.outputSection = this.getOutputSection()
      }
    },

    hasConcept () {
      this.consultation.has_konzept = this.hasConcept
    }
  }
})
export default class ConsultationDetails extends Vue {
  @Prop({ type: [ Number, String ], default: 0 }) id

  tabItems = [
    'Grunddaten',
    'Basisdaten',
    'Risikoanalyse',
    'Vertragsanalyse',
    'Statusgespräch',
    // only tabs for mvp 1.0
    // 'Kundenpräferenzen',
    'Konzeptvorschlag',
  ]

  tabId = 0
  consultation = {}
  isReloaded = false

  inputFields = {}
  inputSection = {}
  inputDataLoaded = false

  outputFields = []
  outputSection = []
  outputGlobals = []
  globalKonceptFields = []

  conceptStatusFields = []
  conceptVertragFields = []

  showPrintModal = false
  hasEmptyField = false
  showLoadingOverlay = false

  get isNewStep () {
    return !this.consultation.step || this.consultation.step === 'new'
  }

  get title () {
    return this.consultation
      ? `Beratung (${ this.consultation.name })`
      : `Beratung`
  }

  get clientName () {
    return this.consultation && this.consultation.company_name
      ? this.consultation.company_name
      : ''
  }

  // mounted () {
  //   this.setIsLoading(true)
  // }

  getFormattedText (str, firstPos, formatter = '###w', decimals = 2) {
    let lastPos = firstPos
    for (let i = firstPos; i < str.length; i++) {
      const char = str.charAt(i)
      if ((char >= '0' && char <= '9') || char === '.') {
        lastPos = i
      } else {
        i = str.length
      }
    }

    const currencyValue = this.formatMoney(str.substr(firstPos, lastPos - firstPos + 1), decimals)
    const newText = str.substr(0, firstPos - 4) + currencyValue + str.substr(lastPos + 1)

    const newPos = newText.indexOf('###w')
    if (newPos >= 0) {
      return this.getFormattedText(newText, newPos + 4, formatter, decimals)
    }

    return newText
  }

  hasErrorFields (section) {
    const sectionData = this.inputSection[section]
    if (!sectionData) {
      return false
    }

    let hasError = false

    if (sectionData.general && sectionData.general.categories.length) {
      const errorCategories = sectionData.general.categories
        .filter(c => c.errorFields && c.errorFields.length)

      hasError = hasError || errorCategories.length > 0
    }

    if (sectionData.locations && sectionData.locations.length) {
      const errorLocations = sectionData.locations
        .filter(l => l.categories.filter(c => c.errorFields && c.errorFields.length).length > 0)

      hasError = hasError || errorLocations.length > 0
    }

    return hasError
  }

  onDataLoaded (data) {
    this.consultation = data
    this.$store.dispatch('setConsultationId', this.consultation.id)
    this.$store.dispatch('setHasConcept', this.consultation.has_konzept)
  }

  onContinue (isReloaded) {
    this.isReloaded = isReloaded

    this.loadFields()
      .then(() => {
        this.tabId = consts.BASE_DATA
        this.$store.dispatch('setCurrentTab', this.tabId)
      })
  }

  onChangeTab (e) {
    if (e === consts.CONCEPT) {
      this.setConceptEdit(false)
    }
    this.hasEmptyField = false
    this.tabId = e
    this.$store.dispatch('setOpenSubSections', [])
    this.$store.dispatch('setCurrentTab', e)
    if (!e) {
      return
    }

    if (e === consts.CONCEPT) {
      this.loadConceptFields()
    } else {
      this.loadFields()
    }
  }

  loadFields () {
    const params = {
      with_rules: this.isDebugMode > 0,
      for_basis: this.tabId === consts.BASE_DATA || this.tabId === 0,
      for_risiko: this.tabId === consts.RISK_ANALYSIS,
      for_vertrag: this.tabId === consts.CONTRACT_ANALYSIS,
      for_status: this.tabId === consts.STATUS_TALK,
      for_konzept: false
    }

    // this.inputFields = {}
    // this.inputSection = {}
    // this.outputSection = []
    this.setIsLoading(true)

    return this.$axios
      .get(`${ Urls.Consultation(this.id) }fields/`, params)
      .then(res => {
        if (this.tabId <= consts.RISK_ANALYSIS) {
          this.inputFields = res.data || {}
          this.inputSection = this.getInputSection(res.data || {})
          this.inputDataLoaded = true
        } else {
          this.outputSection = this.getOutputSection(res.data || {})
        }
      })
      .catch(err => {
        this.$toast.error(err.message, 'Fehler')
      })
      .finally(() => {
        this.setIsLoading(false)
      })
  }

  createConcept (isRecreate = false) {
    this.$confirmDialog.close()
    this.showLoadingOverlay = true

    return this.$axios
      .post(`${ Urls.Consultation(this.id) }create_konzept/`, {
        recreate: isRecreate
      })
      .then(() => {
        this.consultation.has_konzept = true
        this.$store.dispatch('setHasConcept', true)
        if (isRecreate) {
          this.setConceptEdit(false)
        }
        return this.loadConceptFields()
      })
      .catch(err => {
        this.$toast.error(err.message, 'Fehler')
      })
      .finally(() => {
        this.showLoadingOverlay = false
      })
  }

  loadStatusFields () {
    const params = {
      with_rules: this.isDebugMode > 0,
      for_status: true,
      for_konzept: false,
    }

    return this.$axios
      .get(`${ Urls.Consultation(this.id) }fields/`, params)
      .then(res => {
        this.parseFieldData(res.data || {}, null, true)
      })
      .catch(err => {
        this.$toast.error(err.message, 'Fehler')
      })
  }

  loadConceptStatusFields () {
    const params = {
      with_rules: this.isDebugMode > 0,
      for_status: true,
      for_konzept: true,
    }

    this.conceptStatusFields = []

    return this.$axios
      .get(`${ Urls.Consultation(this.id) }fields/`, params)
      .then(res => {
        const parsed = this.parseFieldData(res.data || {})
        this.conceptStatusFields = parsed.general
        this.outputSection = this.getOutputSection()
        this.getSumOfInvestment(parsed.global)
      })
      .catch(err => {
        this.$toast.error(err.message, 'Fehler')
      })
  }

  loadConceptVertragFields (reloadSum = true) {
    const params = {
      with_rules: this.isDebugMode > 0,
      for_vertrag: true,
      for_konzept: true,
    }

    this.conceptVertragFields = []

    return this.$axios
      .get(`${ Urls.Consultation(this.id) }fields/`, params)
      .then(res => {
        const parsed = this.parseFieldData(res.data || {}, [], false, true)
        this.conceptVertragFields = parsed.general
        this.outputSection = this.getOutputSection()
        if (reloadSum) {
          this.getSumOfInvestment(parsed.global)
        }
      })
      .catch(err => {
        this.$toast.error(err.message, 'Fehler')
      })
  }

  loadConceptFields () {
    if (!this.consultation.has_konzept) {
      this.createConcept()
      return
    }

    return this.loadStatusFields()
      .then(() => {
        return this.loadConceptStatusFields()
      })
      // .then(() => {
      //   return this.loadConceptVertragFields()
      // })
      .catch(err => {
        this.$toast.error(err.message, 'Fehler')
      })
  }

  onClickTitle (e) {
    if (!this.isAdmin) {
      return
    }

    if (e.shiftKey) {
      if (this.isDebugMode === 2) {
        this.setDebugMode(0)
      } else {
        this.setDebugMode(2) // show all fields including hidden ones
      }
    } else {
      if (this.isDebugMode === 1) {
        this.setDebugMode(0)
      } else {
        this.setDebugMode(1) // show only debug info
      }
    }
  }

  onAddGroup (id) {
    const handleAddGroup = () => {
      const params = {
        field_id: id,
        with_rules: this.isDebugMode > 0,
        for_basis: this.tabId === consts.BASE_DATA || this.tabId === 0,
        for_risiko: this.tabId === consts.RISK_ANALYSIS,
        for_vertrag: this.tabId === consts.CONTRACT_ANALYSIS,
      }

      this.setIsLoading(true)

      this.$axios
        .post(`${ Urls.Consultation(this.id) }group/`, params)
        .then(res => {
          const updatedIds = this.updateFields(null, res.data)
          // console.log(updatedIds)
          const newFields = res.data
            .filter(f => f.id !== id && !updatedIds.includes(f.id))
            .sort((a, b) => a.sort_index - b.sort_index)

          this.addFieldGroup(id, newFields)
        })
        .catch(err => {
          this.$toast.error(err.message, 'Fehler')
        })
        .finally(() => {
          this.setIsLoading(false)
          this.$confirmDialog.close()
        })
    }

    if (!this.consultation.has_konzept) {
      handleAddGroup()
      return
    }

    this.$confirmDialog.show({
      text: `Für die Beratung existiert bereits ein Konzept.\n` +
        `Wenn Sie die Änderung durchführen, müssen alle Änderungen an dem Konzept erneut durchgeführt werden.\n\n` +
        `Möchten Sie die Änderungen übernehmen?`,
      handleOk: () => {
        handleAddGroup()
        this.deleteConcept()
      },
    })
  }

  onDeleteGroup (fieldId, id) {
    const handleDeleteGroup = () => {
      const params = {
        group_id: id,
      }

      this.setIsLoading(true)

      this.$axios
        .delete(`${ Urls.Consultation(this.id) }group/`, params)
        .then(res => {
          this.deleteFieldGroup(res.data.deleted_ids)

          if (res.data.changed_fields) {
            this.updateFields(null, res.data.changed_fields)
          }
        })
        .catch(err => {
          this.$toast.error(err.message, 'Fehler')
        })
        .finally(() => {
          this.setIsLoading(false)
          this.$confirmDialog.close()
        })
    }

    if (!this.consultation.has_konzept) {
      handleDeleteGroup()
      return
    }

    this.$confirmDialog.show({
      text: `Für die Beratung existiert bereits ein Konzept.\n` +
        `Wenn Sie die Änderung durchführen, müssen alle Änderungen an dem Konzept erneut durchgeführt werden.\n\n` +
        `Möchten Sie die Änderungen übernehmen?`,
      handleOk: () => {
        handleDeleteGroup()
        this.deleteConcept()
      },
    })
  }

  onSetField (id, value) {
    const params = {
      field_id: id,
      value,
      with_rules: this.isDebugMode > 0,
      for_basis: this.tabId === consts.BASE_DATA,
      for_risiko: this.tabId === consts.RISK_ANALYSIS,
      for_vertrag: this.tabId === consts.CONTRACT_ANALYSIS,
      for_konzept: this.tabId === consts.CONCEPT,
    }

    this.setIsLoading(true)

    this.$axios
      .put(`${ Urls.Consultation(this.id) }set_field/`, params)
      .then(res => {
        if (this.tabId === consts.CONTRACT_ANALYSIS || this.tabId === consts.CONCEPT) {
          this.updateOutputFields(params, res.data || [])
        } else {
          this.updateFields(params, res.data || [])
        }
      })
      .catch(err => {
        this.$toast.error(err.message, 'Fehler')

        if (this.tabId <= consts.RISK_ANALYSIS) {
          this.inputSection = this.getInputSection(this.inputFields)
        } else {
          this.outputSection = this.getOutputSection()
        }
      })
      .finally(() => {
        this.setIsLoading(false)
      })
  }

  onRecreateConcept () {
    this.$confirmDialog.show({
      text: 'Durch das "Neu Laden" der Daten werden alle vorhandenen Konzept-Eingaben gelöscht und durch die aktuell in der Beratung gültigen Versicherungsdaten ersetzt.\n\nSind Sie sicher, dass die Daten aktualisiert werden sollen?',
      handleOk: () => {
        this.createConcept(true)
      }
    })
  }

  getInputSection (fieldData) {
    this.hasEmptyField = false

    const categorize = (categories, fields = [], key = 'fields', buildingName = '', buildingId = 0) => {
      fields
        .sort((a, b) => (a.sort_index - b.sort_index))
        .map(f => {
          if (f.type === 'd' && typeof f.v_dropdown === 'string') {
            f.v_dropdown = JSON.parse(f.v_dropdown || '[]')
          }

          f.styling = typeof f.styling === 'string'
            ? JSON.parse(f.styling)
            : f.styling

          f.hidden_org = f.hidden
          if (this.isDebugMode === 2) {
            f.hidden = false
          }

          if (f.hint_text) {
            const currencyPos = f.hint_text.indexOf('###w')
            if (currencyPos > 0) {
              const currencyValue = this.formatMoney(f.hint_text.substring(currencyPos + 4))
              f.hint_text = f.hint_text.substring(0, currencyPos) + ' ' + currencyValue
            }

            if (f.hint_text.indexOf('###red') >= 0) {
              f.hint_text = f.hint_text.replace('###red', '')
              f.hint_class = 'red--text'
            } else if (f.hint_text.indexOf('###green') >= 0) {
              f.hint_text = f.hint_text.replace('###green', '')
              f.hint_class = 'green--text'
            }
          }

          return {
            ...f,
            value: this.getFieldValue(f)
          }
        })
        .forEach((f, _, arr) => {
          if (f.grp_parent_id) {
            return
          }

          if (!categories[f.category]) {
            categories[f.category] = {
              name: f.category,
              sortIndex: consts.MAX_SORT_INDEX,
              fields: [],
              buildings: {},
              hidden: true,
              has_empty: false
            }
          }

          if (f.grp) {
            f.groups = arr.filter(f_ => f.id === f_.grp_parent_id && !f_.hidden)
            if (f.groups.length) {
              if (f.groups.filter(f_ => this.isEmptyField(f_)).length > 0) {
                categories[f.category].has_empty = true
                this.hasEmptyField = true
              }

              f.groups = f.groups.reduce((prev, cur) => {
                const key = cur.grp_id || ''
                prev[key] = prev[key] || []
                prev[key].push(cur)
                return prev
              }, {})
            } else {
              f.groups = null
            }
          }

          if (!categories[f.category]['errorFields']) {
            categories[f.category]['errorFields'] = []
          }

          if (!f.hidden) {
            categories[f.category].hidden = false

            if (this.isEmptyField(f)) {
              categories[f.category].has_empty = true
              this.hasEmptyField = true

              if (f.mandatory) {
                categories[f.category].errorFields.push({
                  name: f.name || f.question,
                  building: buildingName,
                  sub_section: f.sub_section
                })
              }
            }
          }

          if (key === 'fields') {
            categories[f.category][key].push(f)
          } else {
            if (!categories[f.category][key][buildingName]) {
              categories[f.category][key][buildingName] = {
                id: buildingId,
                name: buildingName,
                sortIndex: consts.MAX_SORT_INDEX,
                fields: [],
                hidden: true
              }
            }

            if (!f.hidden) {
              categories[f.category][key][buildingName].hidden = false
            }

            categories[f.category][key][buildingName].fields.push(f)

            if (f.sort_index < categories[f.category][key][buildingName].sortIndex) {
              categories[f.category][key][buildingName].sortIndex = f.sort_index
            }
          }

          if (f.sort_index < categories[f.category].sortIndex) {
            categories[f.category].sortIndex = f.sort_index
          }
        })
    }

    const groupFieldsByCategory = (fields = [], buildings = null) => {
      let categories = {}
      categorize(categories, fields || [], 'fields')

      if (buildings) {
        Object.values(buildings || {}).forEach(b => {
          categorize(categories, b.Fields || [], 'buildings', b.Name, b.ID)
        })
      }

      return Object.keys(categories)
        .sort((a, b) => categories[a].sortIndex - categories[b].sortIndex)
        .map(k => categories[k])
        .filter(c => !c.hidden)
    }

    const fieldData_ = JSON.parse(JSON.stringify(fieldData))

    return {
      general: {
        categories: groupFieldsByCategory(fieldData_.Fields || [])
      },
      locations: Object.values(fieldData_.Locations || {}).map(l => ({
        id: l.ID,
        name: l.Name,
        categories: groupFieldsByCategory(l.Fields, l.Buildings)
      }))
    }
  }

  getOutputLocationFields (fields = [], sectionFields = [], category = '', sub_section = '') {
    const locations = {}

    // parse general fields, locations and buildings
    fields
      .forEach(f => {
        if (f.hidden) {
          return
        }

        // const parentField = f.grp_parent_id
        //   ? sectionFields.find(f_ => f_.id === f.grp_parent_id)
        //   : null

        const locationId = f.location_id
          ? f.location_id
          : '#'

        if (!locations[locationId]) {
          locations[locationId] = {
            id: locationId,
            name: f.location_name,
            sort_index: f.sort_index,
            fields: [],
            buildings: {},
            hidden: true
          }
        }

        const location = locations[locationId]
        if (location.sort_index > f.sort_index) {
          location.sort_index = f.sort_index
        }

        if (!f.building_id) {
          location.fields.push(f)
          // if (parentField) {
          //   const isExist = location.fields.find(f_ => f_.id === parentField.id)
          //   if (!isExist) {
          //     location.fields.push({
          //       ...parentField
          //     })
          //   }
          // }
          return
        }

        if (!location.buildings[f.building_id]) {
          location.buildings[f.building_id] = {
            id: f.building_id,
            name: f.building_name,
            sort_index: f.sort_index,
            fields: [],
            hidden: true
          }
        }

        location.buildings[f.building_id].fields.push(f)
        if (location.buildings[f.building_id].sort_index > f.sort_index) {
          location.buildings[f.building_id].sort_index = f.sort_index
        }

        // if (parentField) {
        //   const isExist = location.buildings[f.building_id].fields.find(f_ => f_.id === parentField.id)
        //   if (!isExist) {
        //     location.buildings[f.building_id].fields.push({
        //       ...parentField
        //     })
        //   }
        // }
      })

    // group fields
    const getGroupedFields = (fields_) => {
      const groupedFields = JSON.parse(JSON.stringify(fields_))

      fields_.forEach(f => {
        if (!f.grp_parent_id) {
          return
        }

        const parentField = sectionFields.find(f_ => f_.id === f.grp_parent_id)
        if (!parentField) {
          return
        }
        const isExist = groupedFields.find(f_ => f_.id === parentField.id)
        if (!isExist) {
          groupedFields.push(parentField)
        }
      })

      return groupedFields
        .filter(f => {
          if (f.grp && f.grp_add_text) {
            return true
          }

          return !f.grp_parent_id && (f.question || f.value || f.hint_text)
        })
        .map(f => {
          if (!f.grp) {
            return f
          }

          f.groups = fields_.filter(f_ => f.id === f_.grp_parent_id && !f_.hidden)
          if (!f.groups.length) {
            f.groups = null
            return f
          }

          f.groups = f.groups.reduce((prev, cur) => {
            const key = cur.grp_id || ''
            prev[key] = prev[key] || []
            prev[key].push(cur)
            return prev
          }, {})

          return f
        })
    }

    // group fields inside the location
    Object.keys(locations).forEach(k => {
      locations[k].fields = getGroupedFields(locations[k].fields)
      if (locations[k].fields.length) {
        locations[k].hidden = false
      }

      Object.keys(locations[k].buildings).forEach(k_ => {
        locations[k].buildings[k_].fields = getGroupedFields(locations[k].buildings[k_].fields)
        if (locations[k].buildings[k_].fields.length) {
          locations[k].hidden = false
          locations[k].buildings[k_].hidden = false
        }
      })
    })

    return {
      fields: [],
      locations: Object.values(locations)
        .sort((a, b) => a.sort_index - b.sort_index)
        .filter(l => !l.hidden)
    }
  }

  getOutputSection (fieldData = null) {
    const parsed = this.parseFieldData(fieldData, this.outputFields, true)
    const sectionFields = parsed.general

    this.outputGlobals = parsed.global
    this.hasEmptyField = false

    const getFieldsOfSection = (allFields, category, subSection = null) => {
      // filter fields belong to category and sub section
      const fields = allFields
        .filter(f => {
          if (f.is_structure) {
            return false
          }
          // if (f.grp && !f.grp_parent_id) {
          //   return false
          // }
          if (f.building_group_nr && !f.show_buildings_below) {
            return false
          }
          if (f.styling && f.styling.position === 'last') {
            return false
          }
          if (!subSection) {
            return (
              (f.secondary_category === category && !f.secondary_subsection) ||
              (f.category === category && !f.sub_section)
            )
          }
          if (f.secondary_category === category && f.secondary_subsection === subSection) {
            return true
          }
          return f.category === category && f.sub_section === subSection
        })

      return {
        has_empty: fields.filter(f => f.has_empty || (f.question && this.isEmptyField(f))).length > 0,
        fields: this.getOutputLocationFields(fields, allFields, category, subSection)
      }
    }

    const getLastFieldsOfCategory = (category) => {
      return sectionFields
        .filter(f => {
          if (f.is_structure) {
            return false
          }
          // if (f.grp && !f.grp_parent_id) {
          //   return false
          // }
          if (f.building_group_nr && !f.show_buildings_below) {
            return false
          }
          if (!f.styling || f.styling.position !== 'last') {
            return false
          }
          return (
            (f.secondary_category === category && !f.secondary_subsection) ||
            (f.category === category && !f.sub_section)
          )
        })
    }

    const getSubSectionsOfCategory = (category) => {
      return sectionFields
        .filter(f => f.is_structure && f.secondary_subsection && f.secondary_category === category && !f.hidden)
        .map(f => {
          const fields = getFieldsOfSection(sectionFields, category, f.secondary_subsection)
          const statusFields = getFieldsOfSection(this.conceptStatusFields, category, f.secondary_subsection)
          const vertragFields = getFieldsOfSection(this.conceptVertragFields, category, f.secondary_subsection)

          let conceptField = null
          if (!this.isConceptEdit) {
            conceptField = this.conceptStatusFields.find(f_ => (
              f_.is_structure &&
              f_.secondary_subsection === f.secondary_subsection &&
              f_.secondary_category === f.secondary_category &&
              !f_.hidden
            ))
            if (conceptField && conceptField.is_structure) {
              conceptField.question = conceptField.secondary_subsection
            }
          }

          return {
            ...f,
            name: f.secondary_subsection,
            sort_index: f.sort_index,
            value: this.getFieldValue(f),
            concept_value: conceptField ? this.getFieldValue(conceptField) : 0,
            // concept_field: conceptField,
            fields: fields.fields,
            status_fields: statusFields.fields,
            vertrag_fields: vertragFields.fields,
            has_empty: fields.has_empty,
            cust_praef: f.cust_praef || ''
          }
        })
    }

    // parse categories and sub sections
    const categories = sectionFields
      .filter(f => f.is_structure && f.secondary_category && !f.secondary_subsection && !f.hidden)
      .filter((f, i, arr) => arr.findIndex(a => a.secondary_category === f.secondary_category) === i)
      .map(f => {
        const fields = getFieldsOfSection(sectionFields, f.secondary_category)
        const statusFields = getFieldsOfSection(this.conceptStatusFields, f.secondary_category)
        const vertragFields = getFieldsOfSection(this.conceptVertragFields, f.secondary_category)
        const subSections = getSubSectionsOfCategory(f.secondary_category)
        const lastFields = getLastFieldsOfCategory(f.secondary_category)

        let conceptField = null
        if (!this.isConceptEdit) {
          conceptField = this.conceptStatusFields.find(f_ => (
            f_.is_structure &&
            f_.secondary_category === f.secondary_category &&
            !f_.secondary_subsection &&
            !f_.hidden
          ))
        }

        let hasEmpty = false

        if (this.isStatus) {
          if (this.showCustomerPref) {
            hasEmpty = subSections.filter(s => !s.cust_praef).length > 0
          } else {
            hasEmpty = false
          }
          this.hasEmptyField = hasEmpty
        } else {
          if (fields.has_empty) {
            this.hasEmptyField = true
            hasEmpty = true
          }

          if (subSections.filter(s => s.has_empty).length > 0) {
            this.hasEmptyField = true
            hasEmpty = true
          }

          if (lastFields.filter(f_ => this.isEmptyField(f_)).length > 0) {
            this.hasEmptyField = true
            hasEmpty = true
          }
        }

        return {
          name: f.secondary_category,
          sort_index: f.sort_index,
          hidden: f.hidden,
          value: this.getFieldValue(f),
          concept_value: conceptField ? this.getFieldValue(conceptField) : 0,
          // concept_field: conceptField,
          subSections: subSections,
          fields: fields.fields,
          status_fields: statusFields.fields,
          vertrag_fields: vertragFields.fields,
          has_empty: hasEmpty,
          last_fields: lastFields
        }
      })

    // console.log(categories)
    return categories
  }

  updateFields (updatedField, newFields = []) {
    const updatedIds = []
    const checkAndUpdate = (oldFields) => {
      if (!oldFields) {
        return null
      }

      return oldFields.map(f => {
        const affected = newFields.find(f_ => f.id === f_.id)
        if (affected) {
          updatedIds.push(f.id)
          return affected
        }

        if (updatedField && f.id === updatedField.field_id) {
          f[this.getValueKey(f.type)] = updatedField.value
        }

        return f
      })
    }

    const sectionFields = this.inputFields
    if (sectionFields.Fields) {
      sectionFields.Fields = checkAndUpdate(sectionFields.Fields)
    }

    if (sectionFields.Locations) {
      Object.keys(sectionFields.Locations).forEach(k => {
        const l = sectionFields.Locations[k]
        l.Fields = checkAndUpdate(l.Fields)

        if (l.Buildings) {
          Object.keys(l.Buildings).forEach(k_ => {
            l.Buildings[k_].Fields = checkAndUpdate(l.Buildings[k_].Fields)
          })
        }
        sectionFields.Locations[k] = l
      })
    }

    this.inputFields = JSON.parse(JSON.stringify(sectionFields))
    this.inputSection = this.getInputSection(this.inputFields)

    return updatedIds
  }

  updateOutputFields (updatedField, newFields = []) {
    if (this.isConceptEdit && this.tabId === consts.CONCEPT) {
      this.loadConceptVertragFields()
      return
    }

    const updatedIds = []
    this.outputFields = this.outputFields
      .map(f => {
        const affected = newFields.find(f_ => f.id === f_.id)
        if (affected) {
          updatedIds.push(f.id)
          return {
            ...f,
            ...affected,
          }
        }

        if (updatedField && f.id === updatedField.field_id) {
          f[this.getValueKey(f.type)] = updatedField.value
        }

        return f
      })

    // add new field
    // newFields.forEach(f => {
    //   if (!updatedIds.includes(f.id)) {
    //     this.outputFields.push(f)
    //   }
    // })

    this.outputFields = JSON.parse(JSON.stringify(this.outputFields))
    this.outputSection = this.getOutputSection()
  }

  addFieldGroup (id, groupFields) {
    let firstGroupField = null

    const checkAddGroup = (oldFields) => {
      if (!oldFields) {
        return null
      }

      const updatedId = oldFields.findIndex(f => f.id === id)
      if (updatedId >= 0) {
        // check & update the field if it's updated
        const field = groupFields.find(f => f.id === id)
        if (field) {
          oldFields[updatedId] = field
        }

        const newFields = groupFields.filter(f => f.id !== id)
        if (newFields.length) {
          const validFields = newFields.filter(f => this.isDebugMode === 2 || !f.hidden)
          if (validFields.length) {
            firstGroupField = validFields[0]
          }
        }

        return [
          ...oldFields,
          ...newFields
        ]
      }
      return oldFields
    }

    const sectionFields = this.inputFields
    if (sectionFields.Fields) {
      sectionFields.Fields = checkAddGroup(sectionFields.Fields)
    }

    if (sectionFields.Locations) {
      Object.keys(sectionFields.Locations).forEach(k => {
        const l = sectionFields.Locations[k]
        l.Fields = checkAddGroup(l.Fields)

        if (l.Buildings) {
          Object.keys(l.Buildings).forEach(k_ => {
            l.Buildings[k_].Fields = checkAddGroup(l.Buildings[k_].Fields)
          })
        }
        sectionFields.Locations[k] = l
      })
    }

    this.inputFields = JSON.parse(JSON.stringify(sectionFields))
    this.inputSection = this.getInputSection(this.inputFields)

    if (firstGroupField) {
      this.$store.dispatch('setOpenGroups', [
        firstGroupField.grp_id
      ])

      setTimeout(() => {
        this.scrollToField(firstGroupField.id)
      }, 300)
    }
  }

  deleteFieldGroup (deletedIds) {
    const checkDeleteGroup = (oldFields) => {
      if (!oldFields) {
        return null
      }

      return oldFields.filter(f => !deletedIds.includes(f.id))
    }

    const sectionFields = this.inputFields
    if (sectionFields.Fields) {
      sectionFields.Fields = checkDeleteGroup(sectionFields.Fields)
    }

    if (sectionFields.Locations) {
      Object.keys(sectionFields.Locations).forEach(k => {
        const l = sectionFields.Locations[k]
        l.Fields = checkDeleteGroup(l.Fields)

        if (l.Buildings) {
          Object.keys(l.Buildings).forEach(k_ => {
            l.Buildings[k_].Fields = checkDeleteGroup(l.Buildings[k_].Fields)
          })
        }
        sectionFields.Locations[k] = l
      })
    }

    this.inputFields = JSON.parse(JSON.stringify(sectionFields))
    this.inputSection = this.getInputSection(this.inputFields)
  }

  onInputCustomerPref (fieldId, value) {
    const params = {
      field_id: fieldId,
      value
    }

    this.setIsLoading(true)

    return this.$axios
      .post(`${ Urls.Consultation(this.id) }set_cust_praef/`, params)
      .then(() => {
        this.outputFields = this.outputFields.map(f => {
          if (f.id === fieldId) {
            f.cust_praef = value
          }
          return f
        })
        this.outputSection = this.getOutputSection()
      })
      .catch(err => {
        this.$toast.error(err.message, 'Fehler')
      })
      .finally(() => {
        this.setIsLoading(false)
      })
  }

  parseFieldData (fieldData, defaultArray = [], isOutputFields = false, isForceChangeLabel = false) {
    let globalFields = []
    let sectionFields = []

    if (!fieldData) {
      sectionFields = JSON.parse(JSON.stringify(defaultArray))
    } else {
      if (fieldData.Fields) {
        sectionFields.push(...fieldData.Fields)
      }

      Object.values(fieldData.Locations || {}).map(l => {
        if (l.Fields) {
          sectionFields.push(...l.Fields.map(f => ({
            ...f,
            location_id: l.ID,
            location_name: l.Name
          })))
        }

        Object.values(l.Buildings || {}).forEach(b => {
          if (b.Fields) {
            sectionFields.push(...b.Fields.map(f => ({
              ...f,
              location_id: l.ID,
              location_name: l.Name,
              building_id: b.ID,
              building_name: b.Name
            })))
          }
        })
      })

      if (isOutputFields) {
        this.outputFields = sectionFields
      }
    }

    sectionFields = sectionFields
      .sort((a, b) => a.sort_index - b.sort_index)
      .map(f => {
        if (f.type === 'd' && typeof f.v_dropdown === 'string') {
          f.v_dropdown = JSON.parse(f.v_dropdown || '[]')
        }

        if (isForceChangeLabel) {
          if (f.question === 'Besteht für das Risiko eine Versicherung?') {
            f.question = 'Soll das Risiko weiterhin versichert sein?'
          } else if (f.question === 'Versicherungsnummer') {
            f.question = 'Angebot'
          }
        }

        f.styling = typeof f.styling === 'string'
          ? JSON.parse(f.styling)
          : f.styling

        f.hidden_org = f.hidden
        if (this.isDebugMode === 2) {
          f.hidden = false
        }

        if (f.grp_parent_id) {
          const parent = sectionFields.find(f_ => f_.id === f.grp_parent_id)
          if (!parent || parent.hidden) {
            f.hidden = true
          }
        }

        if (f.hint_text) {
          let currencyPos = f.hint_text.indexOf('###w') // with decimals
          if (currencyPos > 0) {
            f.hint_text = this.getFormattedText(f.hint_text, currencyPos + 4)
          }

          currencyPos = f.hint_text.indexOf('###W') // without decimals
          if (currencyPos > 0) {
            f.hint_text = this.getFormattedText(f.hint_text, currencyPos + 4, '###W', 0)
          }

          if (f.hint_text.indexOf('###red') >= 0) {
            f.hint_text = f.hint_text.replace('###red', '')
            f.hint_class = 'red--text'
          } else if (f.hint_text.indexOf('###green') >= 0) {
            f.hint_text = f.hint_text.replace('###green', '')
            f.hint_class = 'green--text'
          }
        }

        f.value = this.getFieldValue(f)

        if (!f.category && !f.sub_section && !f.secondary_category && !f.secondary_subsection && !f.hidden) {
          globalFields.push(f)
        }

        return f
      })

    sectionFields = sectionFields.map(f => {
      if (f.show_buildings_below) {
        f.sub_fields = sectionFields.filter(f_ => f_.building_group_nr === f.building_group_nr && !f_.show_buildings_below)
        f.has_empty = f.sub_fields.filter(f_ => this.isEmptyField(f_)).length > 0
        f.sub_fields = this.getOutputLocationFields(f.sub_fields)
      }
      return f
    })

    return {
      general: sectionFields,
      global: globalFields
    }
  }

  getSumOfInvestment (fields) {
    const field = fields.find(f => f.field_id === 'F_399_0_000')
    if (field) {
      this.globalKonceptFields = [
        field
      ]
    }
  }

  toggleShowCustomerPref () {
    this.setShowCustomerPref(!this.showCustomerPref)
    this.outputSection = this.getOutputSection()
  }

  toggleConceptEdit (value) {
    if (value) {
      this.loadConceptVertragFields(false)
    } else {
      this.loadConceptStatusFields()
    }
    this.setConceptEdit(value)
  }
}
</script>

<style lang="sass">
.consultation-details
  .consultation-header
    position: sticky
    top: 0
    align-items: center
    background-color: #fff
    z-index: 9

    .btn-actions
      display: flex
      justify-content: flex-end
      align-items: center

      i
        color: var(--v-primary-base)
        font-size: 22px

      .btn-toggle
        opacity: 0.6

        &.selected
          opacity: 1

  .tab-header
    position: sticky
    top: 51px
    background-color: #fff
    z-index: 9

    &.v-tabs .v-tab
      flex: 1 1 20%

  .location-header
    position: sticky
    top: 99px
    background-color: #fff
    z-index: 9

    &.output a
      min-height: 24px

  .category-header
    position: sticky
    top: 154px
    z-index: 4

    &.output
      top: 99px

  .details-title
    user-select: none

  .group-add-row,
  .group-remove-row
    position: sticky
    background-color: #fff

  .group-add-row
    z-index: 3

  .group-remove-row
    bottom: 0
    z-index: 2

  .svg-right-aligned
    position: absolute
    right: 12px
</style>
